import ImportHelper from '@/utils/chunkImportWrapper'
import DlocalHelper from "@/utils/dlocal-helper"
import cardFormatter from "@/utils/card-formatter";
import { UpperCaseGateway } from '@/models/gateway-type';
import { paymentMethods } from "@/utils/payment-helper";
import { UiErrorCode } from '@/utils/error-codes/ui';
import { PaymentErrorCode } from '@/utils/error-codes/payment';
import logger from '@/utils/global-logger';

const PaymentGatewayError = null // Import Component for Payment Gateway Error
var isDev = process.env.NODE_ENV != "production";
export const CDN = {
    braintree: 'https://js.braintreegateway.com/web/3.96.1/js/client.min.js',
    braintreDataCollector: 'https://js.braintreegateway.com/web/3.96.1/js/data-collector.min.js',
    plaid: 'https://cdn.plaid.com/link/v2/stable/link-initialize.js',
    stripe: 'https://js.stripe.com/v2/',
    applePayViaBraintree: 'https://js.braintreegateway.com/web/3.96.1/js/apple-pay.min.js',
    adyenSelfHosted: `${window.location.origin}${getStaticFolder()}/adyen.encrypt.nodom.min.js`,
    finix:'https://forms.finixpymnts.com/finix.js',
    chargebeejs: (window.initInfo && window.initInfo.chargebee_js_url) || 'https://js.chargebee.com/v2/chargebee.js'
}

let store;
// Dynamically load Store
if(window.portalConfig) {
    import('@/store/portal')
        .then(_store => store = _store.default)
        .catch(e => logger.error(UiErrorCode.BOOTSTRAP_LOAD_FAILED, { storeType: 'portal' }, e))
} else if(window.hpConfig && window.hpConfig.ctype === 'collect_now') {
    import('@/store/paynow')
        .then(_store => store = _store.default)
        .catch(e => logger.error(UiErrorCode.BOOTSTRAP_LOAD_FAILED, { storeType: 'paynow' }, e))
} else if(window.hpConfig && window.hpConfig.ctype === 'claim_gift') {
    import('@/store/claim-gift')
        .then(_store => store = _store.default)
        .catch(e => logger.error(UiErrorCode.BOOTSTRAP_LOAD_FAILED, { storeType: 'claim_gift' }, e))
} else if(window.hpConfig) {
    import('@/store/checkout')
        .then(_store => store = _store.default)
        .catch(e => logger.error(UiErrorCode.BOOTSTRAP_LOAD_FAILED, { storeType: 'checkout' }, e))
}

function has(parent, child) {
  if(parent && (typeof parent === "string" || parent.constructor === Array))
      return !!~parent.indexOf(child)
  return false
}

export function threedsNeeded(gateway_account_id, type, paymentInfo) {
    const pm_list = store.getters.pm_list;
    const gwConfig = store.getters.non_smart_routing_gw_config || {};
    let pm = pm_list.find(gw => ( gw.id == gateway_account_id && gw.type == type));
    if(!pm && type === paymentMethods.CARD && gwConfig[gateway_account_id]) {
        pm = gwConfig[gateway_account_id];
    }
    if(!pm || DlocalHelper.skipThreeDS(pm, store, paymentInfo)) {
        return false;
    }
	if(skipSpreedly3ds(pm, paymentInfo)) {
		return false;
	}
   return !!pm.enabled3ds;
}

export function intentFlowEnabled(gateway_account_id, type) {
    const pm_list = store.getters.pm_list;
    const gwConfig = store.getters.non_smart_routing_gw_config || {};
    let pm = pm_list.find(gw => ( gw.id == gateway_account_id && gw.type == type));
    if(!pm && type === paymentMethods.CARD && gwConfig[gateway_account_id]) {
        pm = gwConfig[gateway_account_id];
    }
   return pm && !!pm.enabled_intent_flow;
}

export function skipSpreedly3ds(pm, paymentInfo) {
    if(pm && pm.spreedly_config && paymentInfo && (paymentInfo.number || (paymentInfo.card && paymentInfo.card.brand))) {
		let allowedBrands = pm.spreedly_config.allowed_3ds_card_networks || [];
		let givenBrand = paymentInfo.number ? cardFormatter.cardType(paymentInfo.number) : paymentInfo.card.brand;
		return allowedBrands.findIndex(brand => brand.toUpperCase() == givenBrand.toUpperCase()) < 0;
    }
	return false;
}

export function checkPaymentGateway() {
    // Fallback configuration
    let fallbackConfig = {
        hasPaypal: true,
        hasBraintree: true,
        hasStripe: true,
        hasAdyen: true,
        hasAmazonPay: true,
        hasApplePayViaBraintree: true,
        hasPaypalViaBraintree: true,
        loadChargebeejs: true,
        hasBraintreeDataCollector: true
    };
    if(!store) return fallbackConfig;
    try {
        const pm_list = store.getters.pm_list
        const gatewayNames = pm_list.map(gw => gw.gateway_name)
        return {
            hasPlaid: pm_list.some(pm => pm.plaid_config),
            loadChargebeejs: pm_list.some(pm => pm.enabled3ds || pm.enabled_intent_flow),
            hasPaypal: has(gatewayNames, UpperCaseGateway.PAYPAL_EXPRESS_CHECKOUT) || has(gatewayNames, UpperCaseGateway.PAYPAL),
            hasBraintree: has(gatewayNames, UpperCaseGateway.BRAINTREE),
            hasStripe: has(gatewayNames, UpperCaseGateway.STRIPE),
            hasAdyen: pm_list.some(pm => (pm.gateway_name === UpperCaseGateway.ADYEN && pm.client_side_encryption_config)),
            hasPayPalViaAdyen: pm_list.some(pm => (pm.gateway_name === UpperCaseGateway.ADYEN && pm.type === 'paypal_express_checkout')),
            hasApplePayViaAdyen: pm_list.some(pm => (pm.gateway_name === UpperCaseGateway.ADYEN && pm.type === 'apple_pay')),
            hasApplePayViaCheckoutCom: pm_list.some(pm => (pm.gateway_name === UpperCaseGateway.CHECKOUT_COM && pm.type === 'apple_pay')),
            hasApplePayViaStripe: pm_list.some(pm => (pm.gateway_name === UpperCaseGateway.STRIPE && pm.type === 'apple_pay')),
            hasApplePayViaMollie: pm_list.some(pm => (pm.gateway_name === UpperCaseGateway.MOLLIE && pm.type === 'apple_pay')),
            hasApplePayViaDeutscheBank: pm_list.some(pm => (pm.gateway_name === UpperCaseGateway.DEUTSCHE_BANK && pm.type === 'apple_pay')),
            hasApplePayViaMoyasar: pm_list.some(pm => (pm.gateway_name === UpperCaseGateway.MOYASAR && pm.type === 'apple_pay')),
            hasAmazonPay: has(gatewayNames, UpperCaseGateway.AMAZON_PAYMENTS),
            hasAmazonPayV2: pm_list.some(pm => (pm.gateway_name !== UpperCaseGateway.AMAZON_PAYMENTS && pm.type === 'amazon_payments')),
            hasPaypalViaBraintree: pm_list.some(pm => (pm.gateway_name === UpperCaseGateway.BRAINTREE && pm.type === 'paypal_express_checkout')),
            hasApplePayViaBraintree: pm_list.some(pm => pm.gateway_name === UpperCaseGateway.BRAINTREE && pm.type === 'apple_pay'),
            hasApplePayViaVantiv: pm_list.some(pm => pm.gateway_name === UpperCaseGateway.VANTIV && pm.type === 'apple_pay'),
            amazonPayWidgetUrl: (pm_list.find(pm => pm.gateway_name === UpperCaseGateway.AMAZON_PAYMENTS) || {configuration: {}}).configuration.widget_url,
            hasBraintreeDataCollector: pm_list.some(pm => (pm.gateway_name === UpperCaseGateway.BRAINTREE && pm.type === 'card')),
            hasVenmo: pm_list.some(pm => ((pm.gateway_name === UpperCaseGateway.BRAINTREE || pm.gateway_name === UpperCaseGateway.PAYPAL)   && pm.type === 'venmo'))
        }
    } catch(e) {
        logger.error(PaymentErrorCode.GATEWAY_CONFIG_FAILED, {}, e);
        return fallbackConfig;
    }
}

function getStaticFolder() {
    var isDev = process.env.NODE_ENV != "production";
    return isDev ? "/static/javascripts" : "/assets/hp_v3/javascripts";
}

function stripeJsVersion2() {
    if(window.Stripe && window.Stripe.version==3) {
        delete window.Stripe;
    }
    ImportHelper.loadScript(CDN.stripe, null, 'Stripe');
}

export default {
    CheckoutCardSection: () => new Promise((resolve, reject) => {
        Promise.resolve()
            .then(() => checkPaymentGateway().hasBraintree
                ? ImportHelper.loadScript(CDN.braintree, null, 'braintree')
                : Promise.resolve()
            ).then(() => checkPaymentGateway().hasStripe
                ? stripeJsVersion2()
                : Promise.resolve()
            ).then(() => checkPaymentGateway().hasAdyen
                ? ImportHelper.loadScript(CDN.adyenSelfHosted, {appendIntoBody: true}, 'adyen')
                : Promise.resolve()
            ).then(() => checkPaymentGateway().hasBraintreeDataCollector
                ? ImportHelper.loadScript(CDN.braintreDataCollector, null, 'dataCollector', window.braintree)
                : Promise.resolve()
            ).then(() => resolve(import('@/components/modules/hosted-pages/CardSection').then(m => m.default)))
    }),
    PortalCardSection: () => new Promise((resolve, reject) => {
        Promise.resolve()
            .then(() => checkPaymentGateway().hasBraintree
                ? ImportHelper.loadScript(CDN.braintree, null, 'braintree')
                : Promise.resolve()
            ).then(() => checkPaymentGateway().hasStripe
                ? stripeJsVersion2()
                : Promise.resolve()
            ).then(() => checkPaymentGateway().hasAdyen
                ? ImportHelper.loadScript(CDN.adyenSelfHosted, {appendIntoBody: true}, 'adyen')
                : Promise.resolve()
            ).then(() => checkPaymentGateway().hasBraintreeDataCollector
                ? ImportHelper.loadScript(CDN.braintreDataCollector, null, 'dataCollector', window.braintree)
                : Promise.resolve()
            ).then(() => resolve(import('@/components/modules/portal/payment-methods/CardSection').then(m => m.default)))
    }),
    PaypalSection: () => new Promise((resolve, reject) => {
        Promise.resolve()
            .then(() => {
                resolve(ImportHelper.loadComponent('PayPalV2Section'));
            })
            .catch(e => {
                logger.error(PaymentErrorCode.COMPONENT_LOAD_FAILED, { component: 'PaypalSection' }, e)
                resolve(PaymentGatewayError)
            })
    }),
    DirectDebitSection: () => new Promise((resolve, reject) => {
        Promise.resolve()
            .then(() => checkPaymentGateway().hasStripe
                ? stripeJsVersion2()
                : Promise.resolve()
            ).then(() => checkPaymentGateway().hasPlaid
                ? ImportHelper.loadScript(CDN.plaid, null, 'Plaid')
                : Promise.resolve()
            ).then(() => resolve(ImportHelper.loadComponent('DirectDebitSection')))
            .catch(e => {
                logger.error(PaymentErrorCode.COMPONENT_LOAD_FAILED, { component: 'DirectDebitSection' }, e)
                resolve(PaymentGatewayError)
            })
    }),
    AmazonPaymentSection:  () => new Promise((resolve, reject) => {
        Promise.resolve()
            .then(() => checkPaymentGateway().hasAmazonPay
                ? ImportHelper.loadScript(checkPaymentGateway().amazonPayWidgetUrl, null, 'amazon')
                : Promise.resolve()
            ).then(() => {
                if (checkPaymentGateway().hasAmazonPay) {
                    resolve(ImportHelper.loadComponent('AmazonPaymentSection'));
                } else {
                    resolve(ImportHelper.loadComponent('AmazonPaymentV2Section'));
                }
            })
            .catch(e => {
                logger.error(PaymentErrorCode.COMPONENT_LOAD_FAILED, { component: 'AmazonPaymentSection' }, e)
                resolve(PaymentGatewayError)
            })
    }),
    ApplePaySection: () => new Promise((resolve, reject) => {
        Promise.resolve()
            .then(() => checkPaymentGateway().hasBraintree
                ? ImportHelper.loadScript(CDN.braintree, null, 'braintree')
                : Promise.resolve()
            ).then(() => checkPaymentGateway().hasApplePayViaBraintree
                ? ImportHelper.loadScript(CDN.applePayViaBraintree, null, 'applePay', window.braintree)
                : Promise.resolve()
            ).then(() => {
                if(checkPaymentGateway().hasApplePayViaStripe || checkPaymentGateway().hasApplePayViaAdyen || checkPaymentGateway().hasApplePayViaCheckoutCom || checkPaymentGateway().hasApplePayViaBraintree || checkPaymentGateway().hasApplePayViaDeutscheBank || checkPaymentGateway().hasApplePayViaMollie || checkPaymentGateway().hasApplePayViaMoyasar || checkPaymentGateway().hasApplePayViaVantiv) {
                    resolve(ImportHelper.loadComponent('ApplePayV2Section'));
                } else {
                    resolve(ImportHelper.loadComponent('ApplePaySection'));
                }
            })
            .catch(e => {
                logger.error(PaymentErrorCode.COMPONENT_LOAD_FAILED, { component: 'ApplePaySection' }, e)
                resolve(PaymentGatewayError)
            })
    }),
    iDealSection: () => new Promise((resolve, reject) => {
        resolve(ImportHelper.loadComponent('IDealSection'))
            .catch(e => {
                logger.error(PaymentErrorCode.COMPONENT_LOAD_FAILED, { component: 'IDealSection' }, e)
                resolve(PaymentGatewayError)
            })
    }),
    OnlineBankingPolandSection: () => new Promise((resolve, reject) => {
      resolve(ImportHelper.loadComponent('OnlineBankingPolandSection'))
        .catch(e => {
          logger.error(PaymentErrorCode.COMPONENT_LOAD_FAILED, { component: 'OnlineBankingPolandSection' }, e)
          resolve(PaymentGatewayError)
        })
    }),
    StablecoinSection: () => new Promise((resolve, reject) => {
        resolve(ImportHelper.loadComponent('StablecoinSection'))
            .catch(e => {
                logger.error(PaymentErrorCode.COMPONENT_LOAD_FAILED, { component: 'StablecoinSection' }, e)
                resolve(PaymentGatewayError)
            })
    }),
    // Merged payment sections from both branches
    KbcPaymentButtonSection: () => new Promise((resolve, reject) => {
        resolve(ImportHelper.loadComponent('KbcPaymentButtonSection'))
            .catch(e => {
                logger.error(PaymentErrorCode.COMPONENT_LOAD_FAILED, { component: 'KbcPaymentButtonSection' }, e)
                resolve(PaymentGatewayError)
            })
    }),
    TwintSection: () => new Promise((resolve, reject) => {
        resolve(ImportHelper.loadComponent('TwintSection'))
           .catch(e => {
                window.logger.e(e)
                resolve(PaymentGatewayError)
            })
    }),
    PaymeSection: () => new Promise((resolve, reject) => {
        resolve(ImportHelper.loadComponent('PaymeSection'))
            .catch(e => {
                window.logger.e(e)
                resolve(PaymentGatewayError)
            })
    }),
    PayCoSection: () => new Promise((resolve, reject) => {
        resolve(ImportHelper.loadComponent('PayCoSection'))
            .catch(e => {
                window.logger.e(e)
                resolve(PaymentGatewayError)
            })
    }),
    GrabPaySection: () => new Promise((resolve, reject) => {
        resolve(ImportHelper.loadComponent('GrabPaySection'))
            .catch(e => {
                window.logger.e(e)
                resolve(PaymentGatewayError)
            })
    }),
    GoPaySection: () => new Promise((resolve, reject) => {
        resolve(ImportHelper.loadComponent('GoPaySection'))
          .catch(e => {
                window.logger.e(e)
                resolve(PaymentGatewayError)
            })
    }),
    SwishSection: () => new Promise((resolve, reject) => {
        resolve(ImportHelper.loadComponent('SwishSection'))
            .catch(e => {
                window.logger.e(e)
                resolve(PaymentGatewayError)
            })
    }),
    CashAppPaySection: () => new Promise((resolve, reject) => {
        resolve(ImportHelper.loadComponent('CashAppPaySection'))
            .catch(e => {
                logger.error(PaymentErrorCode.COMPONENT_LOAD_FAILED, { component: 'CashAppPaySection' }, e)
                resolve(PaymentGatewayError)
            })
    }),
    ElectronicPaymentStandardSection: () => new Promise((resolve, reject) => {
        resolve(ImportHelper.loadComponent('ElectronicPaymentStandardSection'))
            .catch(e => {
                logger.error(PaymentErrorCode.COMPONENT_LOAD_FAILED, { component: 'ElectronicPaymentStandardSection' }, e)
                resolve(PaymentGatewayError)
            })
    }),
    WechatPaySection: () => new Promise((resolve, reject) => {
        resolve(ImportHelper.loadComponent('WechatPaySection'))
            .catch(e => {
                logger.error(PaymentErrorCode.COMPONENT_LOAD_FAILED, { component: 'WechatPaySection' }, e)
                resolve(PaymentGatewayError)
            })
    }),
    TrustlySection: () => new Promise((resolve, reject) => {
        resolve(ImportHelper.loadComponent('TrustlySection'))
            .catch(e => {
                logger.error(PaymentErrorCode.COMPONENT_LOAD_FAILED, { component: 'TrustlySection' }, e)
                resolve(PaymentGatewayError)
            })
    }),
    AlipaySection: () => new Promise((resolve, reject) => {
        resolve(ImportHelper.loadComponent('AlipaySection'))
            .catch(e => {
                logger.error(PaymentErrorCode.COMPONENT_LOAD_FAILED, { component: 'AlipaySection' }, e)
                resolve(PaymentGatewayError)
            })
    }),
    PayByBankSection: () => new Promise((resolve, reject) => {
        resolve(ImportHelper.loadComponent('PayByBankSection'))
            .catch(e => {
                logger.error(PaymentErrorCode.COMPONENT_LOAD_FAILED, { component: 'PayByBankSection' }, e)
                resolve(PaymentGatewayError)
            })
    }),
    RevolutPaySection: () => new Promise((resolve, reject) => {
        resolve(ImportHelper.loadComponent('RevolutPaySection'))
            .catch(e => {
                logger.error(PaymentErrorCode.COMPONENT_LOAD_FAILED, { component: 'RevolutPaySection' }, e)
                resolve(PaymentGatewayError)
            })
    }),
    NaverPaySection: () => new Promise((resolve, reject) => {
        resolve(ImportHelper.loadComponent('NaverPaySection'))
            .catch(e => {
                logger.error(PaymentErrorCode.COMPONENT_LOAD_FAILED, { component: 'NaverPaySection' }, e)
                resolve(PaymentGatewayError)
            })
    }),
    KakaoPaySection: () => new Promise((resolve, reject) => {
        resolve(ImportHelper.loadComponent('KakaoPaySection'))
            .catch(e => {
                logger.error(PaymentErrorCode.COMPONENT_LOAD_FAILED, { component: 'KakaoPaySection' }, e)
                resolve(PaymentGatewayError)
            })
    }),
    SofortSection: () => new Promise((resolve, reject) => {
        resolve(ImportHelper.loadComponent('SofortSection'))
            .catch(e => {
                logger.error(PaymentErrorCode.COMPONENT_LOAD_FAILED, { component: 'SofortSection' }, e)
                resolve(PaymentGatewayError)
            })
    }),
    BancontactSection: () => new Promise((resolve, reject) => {
        resolve(ImportHelper.loadComponent('BancontactSection'))
            .catch(e => {
                logger.error(PaymentErrorCode.COMPONENT_LOAD_FAILED, { component: 'BancontactSection' }, e)
                resolve(PaymentGatewayError)
            })
    }),
    PayconiqByBancontactSection: () => new Promise((resolve, reject) => {
        resolve(ImportHelper.loadComponent('PayconiqByBancontactSection'))
            .catch(e => {
                logger.error(PaymentErrorCode.COMPONENT_LOAD_FAILED, { component: 'PayconiqByBancontactSection' }, e)
                resolve(PaymentGatewayError)
            })
    }),
    GooglePaySection: () => new Promise((resolve, reject) => {
        resolve(ImportHelper.loadComponent('GooglePaySection'))
            .catch(e => {
                logger.error(PaymentErrorCode.COMPONENT_LOAD_FAILED, { component: 'GooglePaySection' }, e)
                resolve(PaymentGatewayError)
            })
    }),
    GiropaySection: () => new Promise((resolve, reject) => {
        resolve(ImportHelper.loadComponent('GiropaySection'))
            .catch(e => {
                logger.error(PaymentErrorCode.COMPONENT_LOAD_FAILED, { component: 'GiropaySection' }, e)
                resolve(PaymentGatewayError)
            })
    }),
    DotpaySection: () => new Promise((resolve, reject) => {
        resolve(ImportHelper.loadComponent('DotpaySection'))
            .catch(e => {
                logger.error(PaymentErrorCode.COMPONENT_LOAD_FAILED, { component: 'DotpaySection' }, e)
                resolve(PaymentGatewayError)
            })
    }),
    OfflinePaymentSection: () => new Promise((resolve, reject) => {
        resolve(ImportHelper.loadComponent('OfflinePaymentSection'))
    }),
    NetbankingSection: () => new Promise((resolve, reject) => {
        resolve(ImportHelper.loadComponent('NetbankingSection'))
        .catch(e => {
            logger.error(PaymentErrorCode.COMPONENT_LOAD_FAILED, { component: 'NetbankingSection' }, e)
            resolve(PaymentGatewayError)
        })
    }),
    UpiSection: () => new Promise((resolve, reject) => {
        resolve(ImportHelper.loadComponent('UpiSection'))
        .catch(e => {
            logger.error(PaymentErrorCode.COMPONENT_LOAD_FAILED, { component: 'UpiSection' }, e)
            resolve(PaymentGatewayError)
        })
    }),
    BoletoSection: () => new Promise((resolve, reject) => {
        resolve(ImportHelper.loadComponent('BoletoSection'))
            .catch(e => {
                logger.error(PaymentErrorCode.COMPONENT_LOAD_FAILED, { component: 'BoletoSection' }, e)
                resolve(PaymentGatewayError)
            })
    }),
    VenmoSection: () => new Promise((resolve,reject)=>{
        resolve(ImportHelper.loadComponent('VenmoSection'))
        .catch(e =>{
            logger.error(PaymentErrorCode.COMPONENT_LOAD_FAILED, { component: 'VenmoSection' }, e)
            resolve(PaymentGatewayError)
        })
    }),
    KlarnaPayNowSection: () => new Promise((resolve,reject)=>{
        resolve(ImportHelper.loadComponent('KlarnaPayNowSection'))
        .catch(e =>{
            logger.error(PaymentErrorCode.COMPONENT_LOAD_FAILED, { component: 'KlarnaPayNowSection' }, e)
            resolve(PaymentGatewayError)
        })
    }),
    KlarnaSection: () => new Promise((resolve, reject) => {
        ImportHelper.loadComponent('KlarnaSection')
        .then(resolve)
        .catch(e => {
            logger.error(PaymentErrorCode.COMPONENT_LOAD_FAILED, { component: 'KlarnaSection' }, e)
            resolve(PaymentGatewayError)
        })
    }),
    PayconiqByBancontactRedirectSection: () => new Promise((resolve, reject) => {
        resolve(ImportHelper.loadComponent('PayconiqByBancontactRedirectSection'))
        .catch(e => {
            logger.error(PaymentErrorCode.COMPONENT_LOAD_FAILED, { component: 'PayconiqByBancontactRedirectSection' }, e)
            resolve(PaymentGatewayError)
        })
    }),
    PixSection: () => new Promise((resolve, reject) => {
        resolve(ImportHelper.loadComponent('PixSection'))
        .catch(e => {
            window.logger.e(e)
            resolve(PaymentGatewayError)
        })
    }),
    SouthKoreanCardsSection: () => new Promise((resolve, reject) => {
        resolve(ImportHelper.loadComponent('SouthKoreanCardsSection'))
        .catch(e => {
           logger.error(PaymentErrorCode.COMPONENT_LOAD_FAILED, { component: 'SouthKoreanCardsSection' }, e)
           resolve(PaymentGatewayError)
        })
    }),

    AlipayHkSection: () => new Promise((resolve, reject) => {
      resolve(ImportHelper.loadComponent('AlipayHkSection'))
        .catch(e => {
          logger.error(PaymentErrorCode.COMPONENT_LOAD_FAILED, { component: 'AlipayHkSection' }, e)
          resolve(PaymentGatewayError)
        })
    }),
    PaypaySection: () => new Promise((resolve, reject) => {
      resolve(ImportHelper.loadComponent('PaypaySection'))
        .catch(e => {
          logger.error(PaymentErrorCode.COMPONENT_LOAD_FAILED, { component: 'PaypaySection' }, e)
          resolve(PaymentGatewayError)
        })
    }),
    GcashSection: () => new Promise((resolve, reject) => {
      resolve(ImportHelper.loadComponent('GcashSection'))
        .catch(e => {
          logger.error(PaymentErrorCode.COMPONENT_LOAD_FAILED, { component: 'GcashSection' }, e)
          resolve(PaymentGatewayError)
        })
    }),
}
